import { useEffect } from 'react';
import { Form, Input, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    DEFAULT_TRUST,
    TRUST_MAX,
    TRUST_MIN,
    formatTrustValue,
    normalizeTrustValue,
    sanitizeTrustInput,
} from '@/utils/trust';

export type TrustEditFormValues = {
    trust: number;
    reason: string;
};

type Props = {
    open: boolean;
    title: React.ReactNode;
    initialTrust?: string | number | null;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: TrustEditFormValues) => void;
};

const NAV_KEYS = new Set(['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

/** 按键后是否会被 sanitize 截断（例如第三位小数） */
const isTrustKeyRejected = (current: string, key: string, start: number, end: number) => {
    const rawNext = `${current.slice(0, start)}${key}${current.slice(end)}`;
    const onlyLegal = rawNext.replace(/[^\d.]/g, '');
    const firstDot = onlyLegal.indexOf('.');
    const legalOneDot =
        firstDot === -1
            ? onlyLegal
            : onlyLegal.slice(0, firstDot + 1) + onlyLegal.slice(firstDot + 1).replace(/\./g, '');

    return sanitizeTrustInput(rawNext) !== legalOneDot;
};

const TrustEditModal: React.FC<Props> = ({ open, title, initialTrust, loading, onCancel, onSubmit }) => {
    const [form] = Form.useForm<{ trust: string; reason: string }>();
    const { formatMessage } = useIntl();

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                trust: formatTrustValue(initialTrust ?? DEFAULT_TRUST),
                reason: '',
            });
        }
    }, [form, initialTrust, open]);

    const handleFinish = (values: { trust: string; reason: string }) => {
        onSubmit({
            reason: values.reason,
            trust: normalizeTrustValue(values.trust),
        });
    };

    return (
        <Modal
            open={open}
            title={title}
            destroyOnClose
            confirmLoading={loading}
            okText={formatMessage({ id: 'b.confirm' })}
            cancelText={formatMessage({ id: 'common.cancel' })}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    name="trust"
                    label={<FormattedMessage id="trust.label" />}
                    getValueFromEvent={(e) => sanitizeTrustInput(String(e?.target?.value ?? ''))}
                    rules={[
                        { required: true, message: formatMessage({ id: 'trust.required' }) },
                        {
                            validator: (_, value) => {
                                if (value === '' || value == null) {
                                    return Promise.reject(new Error(formatMessage({ id: 'trust.required' })));
                                }

                                const num = Number(value);
                                if (!Number.isFinite(num) || num < TRUST_MIN || num > TRUST_MAX) {
                                    return Promise.reject(new Error(formatMessage({ id: 'trust.range' })));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input
                        allowClear
                        inputMode="decimal"
                        placeholder={formatMessage({ id: 'trust.placeholder' })}
                        onKeyDown={(e) => {
                            if (e.ctrlKey || e.metaKey || e.altKey) return;
                            if (NAV_KEYS.has(e.key)) return;

                            const input = e.currentTarget;
                            const start = input.selectionStart ?? input.value.length;
                            const end = input.selectionEnd ?? input.value.length;

                            if (e.key === '.' || e.key === 'Decimal') {
                                if (input.value.includes('.') && start === end) {
                                    e.preventDefault();
                                }
                                return;
                            }

                            if (/^\d$/.test(e.key)) {
                                if (isTrustKeyRejected(input.value, e.key, start, end)) {
                                    e.preventDefault();
                                }
                                return;
                            }

                            // 字母及其他符号无效
                            e.preventDefault();
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name="reason"
                    label={<FormattedMessage id="trust.reason" />}
                    rules={[{ required: true, message: formatMessage({ id: 'trust.reasonRequired' }) }]}
                >
                    <Input.TextArea rows={4} placeholder={formatMessage({ id: 'trust.reasonPlaceholder' })} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TrustEditModal;
